import { useEffect } from 'react';

import keyConstants from '@gameday/utils/keyConstants';

const { TAB } = keyConstants;
const KEYCODE_TAB = 9;

/**
 * Hook to add event listener for tabbing on content inside of the Vertical GamePicker.
 * Idea from https://github.mlbam.net/web-media/section-media-resources/blob/66fe357cf10a647e786e103647c9bc0801b6b2eb/packages/bdvp-common/src/components/Card/useFocusTrap.js
 *
 * @param {boolean} isFocusableContentActive Check if that needs to trap is active
 * @param {boolean} keepFocusTrapInModal If true, the focus will stay within modal and not go back to the openerRef
 * @param {object} openerRef Element that opens up content view
 * @param {boolean} contentRef Element that contains elements to focus on
*/
const useGamePickerFocusTrap = ({
    isFocusableContentActive,
    keepFocusTrapInModal,
    openerRef,
    contentRef,
}) => {

    useEffect(() => {

        if(!isFocusableContentActive) {
            return;
        }

        const focusTrap = (event) => {

            const isTabPressed = (event.key === TAB || event.keyCode === KEYCODE_TAB);

            if (!isTabPressed) {
                return;
            }

            const focusableEls = Array.from(contentRef?.current.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details:not([disabled]), summary:not(:disabled)'
            ));

            if(focusableEls.length === 0) {
                return;
            }

            const firstFocusableEl = focusableEls[0];
            const lastFocusableEl = focusableEls[focusableEls.length - 1];
            const isAtBeginning = document.activeElement === openerRef.current || !focusableEls.includes(document.activeElement);

            if (event.shiftKey) /* shift + tab */ {
                if(isAtBeginning) {
                    lastFocusableEl?.focus();
                    event.preventDefault();
                } else if (document.activeElement === firstFocusableEl) {
                    keepFocusTrapInModal ? lastFocusableEl.focus() : openerRef.current.focus();
                    event.preventDefault();
                }
            } else /* tab */ {
                if(isAtBeginning) {
                    firstFocusableEl?.focus();
                    event.preventDefault();
                } else if (document.activeElement === lastFocusableEl) {
                    keepFocusTrapInModal ? firstFocusableEl.focus() : openerRef.current.focus();
                    event.preventDefault();
                }
            }
        };

        document.addEventListener('keydown', focusTrap);

        return () => {
            document.removeEventListener('keydown', focusTrap);
        };

    }, [
        openerRef,
        contentRef,
        isFocusableContentActive,
        keepFocusTrapInModal
    ]);
};

export default useGamePickerFocusTrap;
